/**
 * LivretParcoursAM.jsx — Livret pédagogique interactif Permis AM Topolino.
 *
 * Rend le contenu du livret maître aligné arrêté 08/11/2012 :
 *  - 5 séquences officielles (S1 à S5) avec navigation par tabs
 *  - 36 fiches thème (Objectif, Je comprends, Pourquoi, Risques, Influences,
 *    Exercice, QCM interactif, Question ouverte, Réflexe sécurité)
 *  - Validation binaire officielle : Acquis / À renforcer
 *  - Sync locale + cloud (useLivretSync)
 *  - Impression, projecteur, deep-links
 *
 * Route : /livrets/parcours-am
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Printer, Save, CheckCircle2, AlertTriangle, Circle,
  ChevronRight, ChevronDown, Sparkles, MessageCircle, Lightbulb, Target,
  Eye, ShieldAlert, BookOpen, Zap, Award, ExternalLink, QrCode, Lock,
  FileDown, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import useSeo from '../../hooks/useSeo';
import useLivretSync from '../../hooks/useLivretSync';
import { ProjectorToggle } from '../../components/ProjectorToggle';
import LivretQrShareModal from '../../components/livrets/LivretQrShareModal';
import GuestSaveReminderModal from '../../components/GuestSaveReminderModal';
import LivretCompletCelebration from '../../components/LivretCompletCelebration';
import { toast } from 'sonner';
import { celebrateUnlock } from '../../utils/celebrateUnlock';
import { useApp } from '../../contexts/AppContext';
import {
  LIVRET_MASTER, SEQUENCES, FICHES, EVAL_FINALE_GROUPS, REFLEXES, GRILLE_ENSEIGNANT,
  VALIDATION_LEVELS, nextValidation,
} from '../../data/livrets/livretParcoursAM';
import { getFicheIllustration } from '../../data/livrets/illustrationsMap';
import { VisuelAllureAM } from '../../components/permis-am/VisuelAllureAM';
import { VisuelReglesCirculationAM } from '../../components/permis-am/VisuelReglesCirculationAM';
import { VisuelThemeCard } from '../../components/permis-am/VisuelThemeCard';
import { VISUAL_CONFIGS } from '../../data/cours/permisAmThemes/visualConfigs';

export default function LivretParcoursAM() {
  const [params, setParams] = useSearchParams();
  const { state, save, saveProfil, syncing } = useLivretSync(LIVRET_MASTER.key);
  const { user } = useApp();
  const profil = state.__profil || {};
  const [qrOpen, setQrOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const isGuestViaShare = !user && params.get('share') === 'c2r';
  const REMINDER_KEY = 'c2r:guest-reminder:parcours-am';
  const COMPLETION_KEY = 'c2r:completion:parcours-am';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const downloadPdfProgression = async () => {
    setPdfLoading(true);
    try {
      const payload = {
        eleve: (profil.eleve || '').trim() || 'Élève',
        enseignant: (profil.enseignant || '').trim() || undefined,
        etablissement: (profil.etablissement || '').trim() || undefined,
        date_debut: (profil.dateDebut || profil.date_debut || '').trim() || undefined,
        signature_eleve: (profil.signatureEleve || profil.signature_eleve || profil.eleve || '').trim() || undefined,
        signature_enseignant: (profil.signatureEnseignant || profil.signature_enseignant || profil.enseignant || '').trim() || undefined,
        bilan: (state.bilan || state.__bilan || '').toString().trim() || undefined,
        total_acquis: stats.acquis,
        total_fiches: stats.total,
        sequences: SEQUENCES.map((s) => ({
          id: s.id,
          num: s.num,
          title: s.title,
          subtitle: s.subtitle,
          color: s.color,
          complete: seqStatus[s.id]?.complete || false,
          fiches: FICHES
            .filter((f) => f.sequence === s.id)
            .map((f) => ({
              id: f.id,
              num: f.num,
              title: f.title,
              sequence: f.sequence,
              status: state[`v-${f.id}`] || 'todo',
              reponse_ouverte: (state[`r-${f.id}`] || state[`open-${f.id}`] || '').toString().slice(0, 400) || undefined,
            })),
        })),
      };
      const res = await fetch(`${BACKEND_URL}/api/livret-parcours-am/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = payload.eleve.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      a.download = `progression-livret-parcours-am-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('PDF téléchargé — bonne suite de formation !');
    } catch (e) {
      toast.error(`Impossible de générer le PDF (${e.message || 'erreur'}).`);
    } finally {
      setPdfLoading(false);
    }
  };

  const activeSeq = params.get('seq') || 's1';
  const setActiveSeq = (id) => {
    const next = new URLSearchParams(params);
    next.set('seq', id);
    setParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useSeo({
    title: 'Livret pédagogique Permis AM — Topolino · Code 2 Roues',
    description: "Livret pédagogique interactif complet pour la formation Permis AM option quadricycle léger (Fiat Topolino). 5 séquences · 36 fiches · validation officielle acquis/à renforcer.",
  });

  const fichesActives = useMemo(
    () => FICHES.filter((f) => f.sequence === activeSeq),
    [activeSeq]
  );

  const stats = useMemo(() => {
    const total = FICHES.length;
    let acquis = 0, renforcer = 0;
    FICHES.forEach((f) => {
      const v = state[`v-${f.id}`];
      if (v === 'acquis') acquis++;
      else if (v === 'renforcer') renforcer++;
    });
    return { total, acquis, renforcer, pct: total > 0 ? Math.round((acquis / total) * 100) : 0 };
  }, [state]);

  /* ── Progression linéaire (Sn+1 débloquée quand Sn est 100 % acquis) ──
     - S1 est toujours accessible.
     - Sn+1 est débloquée dès que toutes les fiches de Sn ont v-XX === 'acquis'.
     - Si l'URL cible une séquence verrouillée, on retombe sur la dernière débloquée. */
  const seqStatus = useMemo(() => {
    const status = {}; // { s1: { total, acquis, complete, unlocked } }
    let previousComplete = true; // S1 démarre toujours débloquée
    SEQUENCES.forEach((s) => {
      const fichesSeq = FICHES.filter((f) => f.sequence === s.id);
      const total = fichesSeq.length;
      const acquis = fichesSeq.filter((f) => state[`v-${f.id}`] === 'acquis').length;
      const complete = total > 0 && acquis === total;
      const unlocked = previousComplete;
      status[s.id] = { total, acquis, complete, unlocked };
      previousComplete = complete;
    });
    return status;
  }, [state]);

  // L'Évaluation finale n'apparaît que lorsque les 5 séquences sont 100 % acquises.
  const allSequencesComplete = useMemo(
    () => SEQUENCES.every((s) => seqStatus[s.id]?.complete),
    [seqStatus]
  );

  /* ── Célébration au déblocage d'une séquence ──
     Détecte la transition `complete: false → true` sur chaque séquence.
     Ignore le premier rendu (pas de célébration pour un état déjà complet
     rechargé depuis le cloud). */
  const prevSeqStatusRef = useRef(null);
  const finaleCelebratedRef = useRef(false);
  useEffect(() => {
    const prev = prevSeqStatusRef.current;
    prevSeqStatusRef.current = seqStatus;
    if (!prev) return; // premier rendu — pas de célébration
    SEQUENCES.forEach((s, idx) => {
      const wasComplete = prev[s.id]?.complete;
      const nowComplete = seqStatus[s.id]?.complete;
      if (!wasComplete && nowComplete) {
        // La séquence vient d'être complétée → on célèbre + on annonce la suivante
        const nextSeq = SEQUENCES[idx + 1];
        if (nextSeq) {
          celebrateUnlock({
            title: `🎉 Séquence S${s.num} terminée !`,
            message: `Tu débloques la séquence S${nextSeq.num} · ${nextSeq.title}`,
            color: nextSeq.color,
            accentColors: [s.color, nextSeq.color, '#fbbf24', '#facc15', '#ffffff'],
          });
        } else {
          // Dernière séquence terminée → célébration finale
          celebrateUnlock({
            title: '🏁 Parcours AM complet !',
            message: 'Toutes les séquences sont validées — tu peux passer à l\'évaluation finale.',
            color: s.color,
            durationMs: 4200,
            accentColors: ['#fbbf24', '#facc15', '#10b981', '#22d3ee', '#f43f5e', '#ffffff'],
          });
        }
      }
    });
    // Célébration une fois seulement quand les 5 séquences ET l'évaluation
    // deviennent débloquées ensemble (déclenchement idempotent via ref).
    if (allSequencesComplete && !finaleCelebratedRef.current) {
      finaleCelebratedRef.current = true;
    } else if (!allSequencesComplete && finaleCelebratedRef.current) {
      finaleCelebratedRef.current = false; // reset si régression
    }
  }, [seqStatus, allSequencesComplete]);

  // Auto-redirect si la séquence courante est verrouillée
  useEffect(() => {
    const cur = seqStatus[activeSeq];
    if (cur && !cur.unlocked) {
      // Trouver la dernière séquence débloquée
      const lastUnlocked = [...SEQUENCES].reverse().find((s) => seqStatus[s.id]?.unlocked) || SEQUENCES[0];
      const next = new URLSearchParams(params);
      next.set('seq', lastUnlocked.id);
      setParams(next, { replace: true });
      toast.info(`La séquence ${activeSeq.toUpperCase()} est verrouillée — termine d'abord la précédente pour la débloquer.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSeq, seqStatus]);

  /* ── Auto-rappel sauvegarde pour élève invité (via QR share) ──
     Se déclenche une seule fois quand l'élève atteint 5 fiches acquis
     (localStorage pour éviter la répétition sur les recharges).
     On ne l'affiche pas si la célébration finale est déjà en cours (36/36). */
  useEffect(() => {
    if (!isGuestViaShare) return;
    if (stats.acquis < 5) return;
    if (stats.acquis === stats.total) return; // pas de double avec la célébration finale
    try {
      if (localStorage.getItem(REMINDER_KEY) === '1') return;
      setReminderOpen(true);
      localStorage.setItem(REMINDER_KEY, '1');
    } catch (_) {
      if (stats.acquis === 5) setReminderOpen(true);
    }
  }, [isGuestViaShare, stats.acquis, stats.total]);

  /* ── Célébration finale 36/36 ──
     Se déclenche quand toutes les fiches sont acquises. Idempotent via
     localStorage pour ne pas rejouer à chaque rechargement. Reset possible
     si l'élève régresse (fiche remise "à évaluer"). */
  useEffect(() => {
    if (!allSequencesComplete) {
      // reset possible si l'élève repasse en dessous de 100%
      try {
        if (localStorage.getItem(COMPLETION_KEY) === '1' && stats.acquis < stats.total) {
          localStorage.removeItem(COMPLETION_KEY);
        }
      } catch (_) {}
      return;
    }
    try {
      if (localStorage.getItem(COMPLETION_KEY) === '1') return;
      localStorage.setItem(COMPLETION_KEY, '1');
    } catch (_) {}
    // Ouvre la célébration finale — le celebrateUnlock du dernier passage
    // aura déjà tiré une salve mais on veut un modal + carte souvenir
    setCelebrationOpen(true);
  }, [allSequencesComplete, stats.acquis, stats.total]);

  const setProfil = (partial) => saveProfil({ ...profil, ...partial });

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" data-testid="livret-parcours-am">
      {/* Sticky topbar */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur print:hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="outline" size="sm">
            <Link to="/permis-am/voiturette" aria-label="Retour au Hub Voiturette">
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Hub Voiturette</span>
            </Link>
          </Button>
          <div className="flex-1 text-center min-w-0">
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-rose-500 truncate whitespace-nowrap">
              Livret pédagogique · Permis AM Topolino
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setQrOpen(true)}
            className="inline-flex"
            data-testid="btn-qr-share"
            title="Partager le livret par QR code"
          >
            <QrCode className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">QR élève</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadPdfProgression}
            disabled={pdfLoading}
            className="hidden sm:inline-flex border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/60"
            data-testid="btn-pdf-progression"
            title="Télécharger le récapitulatif PDF de progression"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
            <span>PDF</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="hidden sm:inline-flex" data-testid="btn-print">
            <Printer className="w-4 h-4 mr-1" /> Imprimer
          </Button>
          <ProjectorToggle testid="proj-livret-parcours" />
          <Button asChild variant="ghost" size="sm">
            <Link to="/" aria-label="Retour à l'accueil"><Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Accueil</span></Link>
          </Button>
        </div>
      </div>

      <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        {/* Hero */}
        <header className="mb-8 lg:mb-10 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-[10px] font-black uppercase tracking-widest text-rose-400 mb-3">
            <Award className="w-3 h-3" /> Livret pédagogique interactif · v.{LIVRET_MASTER.meta.version}
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {LIVRET_MASTER.meta.title}
          </h1>
          <p className="text-base lg:text-lg text-rose-500 font-black tracking-wide">{LIVRET_MASTER.meta.subtitle}</p>
          <p className="mt-2 text-white/60 italic">{LIVRET_MASTER.meta.tagline}</p>
          {syncing && <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-400 font-black"><Save className="w-3 h-3 animate-pulse" /> Sauvegarde…</div>}
        </header>

        {/* CTA élève invité (via QR partagé sans compte) — invite à créer un compte pour sauver la progression */}
        {isGuestViaShare && (
          <div
            className="mb-6 rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-r from-emerald-500/15 via-emerald-500/8 to-transparent p-4 sm:p-5 print:hidden"
            data-testid="guest-save-cta"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/25 border border-emerald-500/40 flex items-center justify-center">
                  <Save className="w-5 h-5 text-emerald-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-0.5">
                    Ton livret est ouvert — pas besoin de compte
                  </div>
                  <div className="text-sm text-white/85 leading-snug">
                    Tu peux <strong className="text-white">cocher, répondre et écrire</strong> tout de suite.{' '}
                    <span className="text-white/60">Crée un compte gratuit pour <em className="not-italic font-semibold text-emerald-300">sauvegarder ta progression</em> et la retrouver sur tous tes appareils.</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 sm:flex-shrink-0">
                <Button
                  asChild
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-400 text-white font-black uppercase tracking-wider shadow-lg"
                  data-testid="guest-save-cta-signup"
                >
                  <Link to={`/register?next=${encodeURIComponent(`/livrets/parcours-am?seq=${activeSeq}`)}`}>
                    <Sparkles className="w-4 h-4 mr-1" /> Créer un compte
                  </Link>
                </Button>
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="border-white/25 text-white/85 hover:bg-white/5"
                  data-testid="guest-save-cta-login"
                >
                  <Link to={`/login?next=${encodeURIComponent(`/livrets/parcours-am?seq=${activeSeq}`)}`}>
                    Se connecter
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Identification + progression */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <div className="md:col-span-2 p-4 rounded-2xl border border-white/10 bg-white/[0.02] print:border-black" data-testid="profil-box">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-3 print:text-black">Identification</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <ProfilInput label="Élève" value={profil.eleve} onChange={(v) => setProfil({ eleve: v })} testid="profil-eleve" />
              <ProfilInput label="Enseignant" value={profil.enseignant} onChange={(v) => setProfil({ enseignant: v })} testid="profil-enseignant" />
              <ProfilInput label="Établissement" value={profil.etablissement} onChange={(v) => setProfil({ etablissement: v })} testid="profil-etablissement" />
              <ProfilInput label="Date de début" type="date" value={profil.dateDebut} onChange={(v) => setProfil({ dateDebut: v })} testid="profil-date-debut" />
            </div>
          </div>
          <div className="p-4 rounded-2xl border-2 border-emerald-500/30 bg-emerald-500/5">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">Progression</div>
            <div className="text-3xl sm:text-4xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {stats.pct}<span className="text-emerald-400">%</span>
            </div>
            <div className="mt-1 text-[11.5px] text-white/70">
              <span className="text-emerald-400 font-black">{stats.acquis}</span> acquis · <span className="text-amber-400 font-black">{stats.renforcer}</span> à renforcer · <span className="text-white/50">sur {stats.total} thèmes</span>
            </div>
            <div className="mt-2 h-2.5 rounded-full bg-black/40 border border-white/10 overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${stats.pct}%`, background: 'linear-gradient(90deg,#10b981,#22c55e)' }} />
            </div>
          </div>
        </div>

        {/* Méthode Code 2 Roues */}
        <section className="mb-8 p-5 rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-600/10 via-amber-500/5 to-transparent" data-testid="methode">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-5 h-5 text-amber-400" />
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400">La méthode Code 2 Roues</div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            REGARDER → ANTICIPER → ADAPTER → AGIR
          </div>
          <div className="grid gap-1.5 text-[13.5px] text-white/80">
            <div>1. Qu'est-ce que je vois ?</div>
            <div>2. Qu'est-ce que je ne vois pas ?</div>
            <div>3. Qu'est-ce qui pourrait arriver ?</div>
            <div>4. Que dois-je faire pour rester en sécurité ?</div>
          </div>
        </section>

        {/* Tabs des séquences — progression linéaire, S2+ verrouillées tant que Sn n'est pas terminée */}
        <div className="mb-6 flex flex-wrap gap-2 print:hidden" role="tablist" aria-label="Séquences">
          {SEQUENCES.map((s) => {
            const active = s.id === activeSeq;
            const st = seqStatus[s.id] || { unlocked: true, complete: false, acquis: 0, total: 0 };
            const locked = !st.unlocked;
            const complete = st.complete;
            return (
              <button
                key={s.id}
                role="tab"
                aria-selected={active}
                aria-disabled={locked}
                disabled={locked}
                onClick={() => {
                  if (locked) {
                    toast.info(`Termine la séquence précédente (100 % acquis) pour débloquer S${s.num}.`);
                    return;
                  }
                  setActiveSeq(s.id);
                }}
                title={
                  locked
                    ? `Verrouillée — termine d'abord la séquence précédente`
                    : `S${s.num} · ${st.acquis}/${st.total} acquis${complete ? ' · terminée' : ''}`
                }
                className={`relative px-3 py-2 rounded-full text-[11.5px] font-black uppercase tracking-widest transition border inline-flex items-center gap-1.5 ${
                  active
                    ? 'text-white shadow-lg'
                    : locked
                      ? 'text-white/35 border-white/10 bg-white/[0.02] cursor-not-allowed'
                      : 'text-white/70 border-white/15 bg-white/[0.05] hover:bg-white/[0.08]'
                }`}
                style={active ? { background: s.color, borderColor: s.color } : undefined}
                data-testid={`tab-${s.id}`}
              >
                {locked ? (
                  <Lock className="w-3.5 h-3.5" />
                ) : (
                  <span>{s.emoji}</span>
                )}
                <span>S{s.num} · {s.title}</span>
                {complete && !locked && (
                  <CheckCircle2
                    className={`w-3.5 h-3.5 ${active ? 'text-white' : 'text-emerald-400'}`}
                    aria-label="Séquence terminée"
                  />
                )}
                {/* Mini badge progression pour la séquence active */}
                {active && st.total > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/25 text-[9.5px] font-black">
                    {st.acquis}/{st.total}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Bandeau explicatif progression linéaire */}
        <div className="mb-4 print:hidden">
          <div className="flex items-center gap-2 text-[11.5px] text-white/60 leading-snug">
            <Lock className="w-3.5 h-3.5 text-rose-400/70 flex-shrink-0" />
            <span>
              <strong className="text-white/85">Progression pas à pas :</strong>{' '}
              la séquence suivante se débloque quand toutes les fiches de la précédente sont marquées <em className="text-emerald-400 not-italic font-black">Acquis</em>.
            </span>
          </div>
        </div>

        {/* Fiches de la séquence active */}
        <div className="space-y-4">
          {fichesActives.map((f) => (
            <FicheCard key={f.id} fiche={f} state={state} save={save} />
          ))}
        </div>

        {/* Bloc éval finale — verrouillé tant que les 5 séquences ne sont pas 100 % acquises */}
        {allSequencesComplete ? (
          <EvaluationFinale state={state} save={save} />
        ) : (
          <EvaluationFinaleLocked seqStatus={seqStatus} stats={stats} />
        )}

        {/* 10 réflexes finaux */}
        <ReflexesFinaux />

        {/* Grille enseignant officielle */}
        <GrilleEnseignant state={state} save={save} />

        {/* Bilan + signatures */}
        <BilanFinal profil={profil} setProfil={setProfil} />

        {/* Références */}
        <footer className="mt-10 p-4 rounded-xl border border-white/10 bg-white/[0.02] text-[11.5px] text-white/60 print:text-black">
          <div className="font-black text-white/80 mb-2 print:text-black flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Références</div>
          <p><strong>Référentiel officiel :</strong> {LIVRET_MASTER.references.referentiel}</p>
          <p className="mt-1"><strong>Véhicule :</strong> {LIVRET_MASTER.references.vehicule}</p>
          <p className="mt-1 italic">{LIVRET_MASTER.references.disclaimer}</p>
        </footer>
      </article>

      {/* Modale QR — partage du livret sur smartphone élève
          On force la séquence de destination à S1 (progression pas à pas) :
          l'élève commence toujours par le début, indépendamment de l'onglet
          actif du prof. Les séquences suivantes se débloqueront au fur et à
          mesure de sa progression. */}
      <LivretQrShareModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        livretPath="/livrets/parcours-am"
        livretTitle={LIVRET_MASTER.meta.title}
        livretSubtitle={LIVRET_MASTER.meta.subtitle}
        sequenceId="s1"
        sequenceLabel={`S1 · ${SEQUENCES[0]?.title || 'Démarrage'}`}
        sequenceHint="Pédagogie linéaire · l'élève démarre en S1"
        accentColor="#f43f5e"
        tabletMode
      />

      {/* Modale de rappel : incite l'élève invité à créer un compte à 5 fiches acquises */}
      <GuestSaveReminderModal
        open={reminderOpen}
        onClose={() => setReminderOpen(false)}
        livretPath={`/livrets/parcours-am?seq=${activeSeq}`}
        livretName={LIVRET_MASTER.meta.title}
        acquis={stats.acquis}
        total={stats.total}
      />

      {/* Célébration finale — quand l'élève atteint 36/36 acquis */}
      <LivretCompletCelebration
        open={celebrationOpen}
        onClose={() => setCelebrationOpen(false)}
        eleveName={profil.eleve || 'Élève'}
        livretTitle={LIVRET_MASTER.meta.title}
        totalFiches={stats.total}
        onDownloadPdf={downloadPdfProgression}
        pdfLoading={pdfLoading}
        hubPath="/permis-am/voiturette"
        accentColor="#f43f5e"
      />
    </div>
  );
}

/* ── Composants internes ────────────────────────────────────────────────── */

function ProfilInput({ label, value, onChange, type = 'text', testid }) {
  return (
    <label className="block">
      <div className="text-[11px] font-black uppercase tracking-widest text-white/60 mb-1 print:text-black">{label}</div>
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black" data-testid={testid} />
    </label>
  );
}

function FicheCard({ fiche, state, save }) {
  const [open, setOpen] = useState(true);
  const seqMeta = SEQUENCES.find((s) => s.id === fiche.sequence);
  const validation = state[`v-${fiche.id}`] || 'todo';
  const vMeta = VALIDATION_LEVELS.find((v) => v.key === validation);
  const toggleValidation = () => save({ [`v-${fiche.id}`]: nextValidation(validation) });
  const illu = getFicheIllustration(fiche.id);

  return (
    <section
      id={fiche.id}
      className="rounded-2xl border-2 overflow-hidden bg-white/[0.02] print:border-black print:bg-white print:break-inside-avoid"
      style={{ borderColor: `${seqMeta.color}55` }}
      data-testid={`fiche-${fiche.id}`}
    >
      <header className="px-4 py-3 flex items-center gap-3 flex-wrap" style={{ background: `${seqMeta.color}15` }}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white text-sm shrink-0" style={{ background: seqMeta.color }}>
          {fiche.num}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest print:text-black" style={{ color: seqMeta.color }}>
            {fiche.emoji} Thème {fiche.num} · S{seqMeta.num}
          </div>
          <h2 className="text-lg lg:text-xl font-black text-white leading-tight print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {fiche.title}
          </h2>
        </div>
        {/* Validation binaire officielle */}
        <button
          type="button"
          onClick={toggleValidation}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-black text-[11px] uppercase tracking-widest text-white print:border print:border-black print:text-black print:bg-white"
          style={{ background: vMeta.color }}
          data-testid={`validation-${fiche.id}`}
          title="Cliquer pour changer la validation"
        >
          {validation === 'acquis' && <CheckCircle2 className="w-4 h-4" />}
          {validation === 'renforcer' && <AlertTriangle className="w-4 h-4" />}
          {validation === 'todo' && <Circle className="w-4 h-4" />}
          {vMeta.short}
        </button>
        <button onClick={() => setOpen((o) => !o)} className="print:hidden text-white/60 hover:text-white" aria-label={open ? 'Réduire' : 'Développer'}>
          {open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
      </header>

      {(open || true) && (
        <div className={`${open ? 'block' : 'hidden'} print:block`}>
          {/* Illustration officielle en tête de fiche (si disponible) */}
          {illu && (
            <div className="relative bg-white border-b-2 print:break-inside-avoid" style={{ borderColor: `${seqMeta.color}44` }}>
              {illu.custom === 'allure-am' ? (
                <div className="max-w-3xl mx-auto"><VisuelAllureAM variant="focus" /></div>
              ) : illu.custom === 'regles-circulation-am' ? (
                <div className="max-w-3xl mx-auto"><VisuelReglesCirculationAM variant="focus" /></div>
              ) : illu.config && VISUAL_CONFIGS[illu.config] ? (
                <div className="max-w-3xl mx-auto"><VisuelThemeCard config={VISUAL_CONFIGS[illu.config]} variant="focus" color={seqMeta.color} /></div>
              ) : (
                <img
                  src={illu.src}
                  alt={illu.alt}
                  loading="lazy"
                  className="w-full h-auto max-h-[280px] lg:max-h-[340px] object-contain mx-auto block"
                  data-testid={`fiche-illu-${fiche.id}`}
                />
              )}
              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-widest text-white shadow" style={{ background: seqMeta.color }}>
                {fiche.emoji} Thème {fiche.num}
              </div>
            </div>
          )}
          <div className="px-4 py-4 space-y-4">
          <Section icon={Target} tone="rose" title="Objectif">{fiche.objectif}</Section>

          {fiche.comprendre && (
            <Section icon={BookOpen} tone="sky" title="Je comprends">
              {Array.isArray(fiche.comprendre)
                ? <ul className="list-disc pl-5 space-y-1">{fiche.comprendre.map((p, i) => <li key={i}>{p}</li>)}</ul>
                : <p>{fiche.comprendre}</p>}
            </Section>
          )}

          {fiche.pourquoi && fiche.pourquoi.length > 0 && (
            <Section icon={Lightbulb} tone="amber" title="Pourquoi ?">
              {Array.isArray(fiche.pourquoi) ? <ul className="list-disc pl-5 space-y-1">{fiche.pourquoi.map((p, i) => <li key={i}>{p}</li>)}</ul> : <p>{fiche.pourquoi}</p>}
            </Section>
          )}

          {fiche.risques && fiche.risques.length > 0 && (
            <Section icon={ShieldAlert} tone="rose" title="Risques">
              <ul className="list-disc pl-5 space-y-1">{fiche.risques.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </Section>
          )}

          {fiche.influences && <Influences influences={fiche.influences} />}

          {fiche.note && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-[13.5px] italic print:bg-white print:text-black print:border-black">
              💡 {fiche.note}
            </div>
          )}

          {fiche.exercice && (
            <Section icon={Zap} tone="emerald" title={`🚗 ${fiche.exercice.titre}`}>
              <ul className="list-disc pl-5 space-y-1">{fiche.exercice.items.map((it, i) => <li key={i}>{it}</li>)}</ul>
            </Section>
          )}

          {fiche.scenarios && (
            <Section icon={MessageCircle} tone="violet" title="🎯 Mises en situation">
              <ol className="list-decimal pl-5 space-y-3">
                {fiche.scenarios.map((sc, i) => (
                  <li key={i} className="space-y-1">
                    <div className="text-white/85 print:text-black">{sc}</div>
                    <Textarea
                      rows={2}
                      value={state[`scenario-${fiche.id}-${i}`] || ''}
                      onChange={(e) => save({ [`scenario-${fiche.id}-${i}`]: e.target.value })}
                      placeholder="Réponse de l'élève / notes enseignant…"
                      className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black"
                      data-testid={`scenario-${fiche.id}-${i}`}
                    />
                  </li>
                ))}
              </ol>
            </Section>
          )}

          {fiche.question && (
            <Section icon={MessageCircle} tone="sky" title="🗣️ Question à l'élève">
              <p className="italic mb-2">{fiche.question}</p>
              <Textarea
                rows={2}
                value={state[`q-${fiche.id}`] || ''}
                onChange={(e) => save({ [`q-${fiche.id}`]: e.target.value })}
                placeholder="Réponse de l'élève…"
                className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black"
                data-testid={`textarea-${fiche.id}`}
              />
            </Section>
          )}

          {fiche.qcm && fiche.qcm.length > 0 && (
            <QCM questions={fiche.qcm} ficheId={fiche.id} state={state} save={save} />
          )}

          {fiche.reflexe && (
            <div className="p-4 rounded-xl bg-gradient-to-br from-rose-600/15 via-rose-500/5 to-transparent border-2 border-rose-500/30 flex items-start gap-3 print:break-inside-avoid">
              <Sparkles className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-1 print:text-black">🧠 Mon réflexe sécurité</div>
                <div className="text-base sm:text-lg font-black text-white print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{fiche.reflexe}</div>
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </section>
  );
}

function Section({ icon: Icon, tone = 'sky', title, children }) {
  const toneCls = {
    sky:     { border: 'border-sky-500/25',    text: 'text-sky-400',    bg: 'bg-sky-500/5' },
    rose:    { border: 'border-rose-500/25',   text: 'text-rose-400',   bg: 'bg-rose-500/5' },
    amber:   { border: 'border-amber-500/25',  text: 'text-amber-400',  bg: 'bg-amber-500/5' },
    emerald: { border: 'border-emerald-500/25',text: 'text-emerald-400',bg: 'bg-emerald-500/5' },
    violet:  { border: 'border-violet-500/25', text: 'text-violet-400', bg: 'bg-violet-500/5' },
  }[tone];
  return (
    <div className={`p-3 rounded-lg border ${toneCls.border} ${toneCls.bg} print:bg-white print:border-black`}>
      <div className={`flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-1.5 ${toneCls.text} print:text-black`}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="text-[13.5px] text-white/85 leading-relaxed print:text-black">{children}</div>
    </div>
  );
}

function Influences({ influences }) {
  const cats = [
    { key: 'moi',           title: 'MOI',           color: '#f97316', emoji: '🙋' },
    { key: 'autres',        title: 'LES AUTRES',    color: '#8b5cf6', emoji: '👥' },
    { key: 'environnement', title: "L'ENVIRONNEMENT",color: '#0ea5e9', emoji: '🌦️' },
    { key: 'vehicule',      title: 'LE VÉHICULE',   color: '#22c55e', emoji: '🚗' },
  ];
  const hasAny = cats.some((c) => (influences[c.key] || []).length > 0);
  if (!hasAny) return null;
  return (
    <div className="p-3 rounded-lg border border-violet-500/25 bg-violet-500/5 print:bg-white print:border-black">
      <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-2 text-violet-400 print:text-black">
        🧠 Ce qui peut m'influencer
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {cats.map((c) => {
          const items = influences[c.key] || [];
          if (items.length === 0) return null;
          return (
            <div key={c.key} className="rounded-lg p-2.5 border" style={{ borderColor: `${c.color}44`, background: `${c.color}0f` }}>
              <div className="text-[10px] font-black uppercase tracking-widest mb-1.5 print:text-black" style={{ color: c.color }}>
                {c.emoji} {c.title}
              </div>
              <ul className="space-y-0.5 text-[12.5px] text-white/85 print:text-black">
                {items.map((it, i) => <li key={i} className="flex gap-1.5"><span>·</span>{it}</li>)}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QCM({ questions, ficheId, state, save }) {
  return (
    <div className="p-3 rounded-lg border border-emerald-500/25 bg-emerald-500/5 print:bg-white print:border-black">
      <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-2 text-emerald-400 print:text-black">
        📝 Je me teste
      </div>
      <div className="space-y-3">
        {questions.map((q, qi) => {
          const answered = state[`qcm-${ficheId}-${qi}`];
          return (
            <div key={qi} className="p-2.5 rounded bg-black/30 border border-white/10 print:bg-white print:border-black">
              <div className="text-[13.5px] font-black text-white mb-1.5 print:text-black">Q{qi + 1}. {q.q}</div>
              <div className="grid gap-1">
                {q.choix.map((c, ci) => {
                  const letter = String.fromCharCode(65 + ci);
                  const isPicked = answered === letter;
                  const isBonne = q.bonne === letter;
                  const showResult = !!answered;
                  const cls = !showResult
                    ? 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] text-white/85'
                    : isBonne
                      ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-100'
                      : isPicked
                        ? 'bg-rose-500/10 border-rose-500/40 text-rose-100'
                        : 'bg-white/[0.03] border-white/10 text-white/60';
                  return (
                    <button
                      key={ci}
                      type="button"
                      onClick={() => save({ [`qcm-${ficheId}-${qi}`]: letter })}
                      className={`text-left px-2.5 py-1.5 rounded border text-[13px] transition ${cls} print:bg-white print:text-black print:border-black`}
                      data-testid={`qcm-${ficheId}-${qi}-${letter}`}
                    >
                      <span className="font-black mr-1.5">{letter}.</span>{c}
                      {showResult && isBonne && <CheckCircle2 className="w-3.5 h-3.5 inline ml-1.5 text-emerald-300" />}
                    </button>
                  );
                })}
              </div>
              {answered && q.explication && (
                <div className="mt-2 text-[12px] italic text-emerald-200 print:text-black">💡 {q.explication}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EvaluationFinale({ state, save }) {
  return (
    <section className="mt-10 p-5 rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-600/10 via-emerald-500/5 to-transparent print:break-before-page" data-testid="evaluation-finale">
      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1 print:text-black">🏁 Évaluation finale</div>
      <h2 className="text-2xl font-black text-white mb-4 print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Je sais conduire la Topolino</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {EVAL_FINALE_GROUPS.map((g) => (
          <div key={g.id} className="p-4 rounded-xl border border-white/10 bg-black/20 print:bg-white print:border-black">
            <div className="text-[11px] font-black uppercase tracking-widest text-emerald-400 mb-2 print:text-black">{g.title}</div>
            <ul className="space-y-1.5">
              {g.items.map((it) => {
                const v = state[`ef-${it.id}`] || 'todo';
                const meta = VALIDATION_LEVELS.find((x) => x.key === v);
                return (
                  <li key={it.id} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => save({ [`ef-${it.id}`]: nextValidation(v) })}
                      className="w-6 h-6 rounded-full border-2 shrink-0 text-white flex items-center justify-center text-[10px] font-black"
                      style={{ background: v === 'todo' ? 'transparent' : meta.color, borderColor: meta.color, color: v === 'todo' ? meta.color : 'white' }}
                      data-testid={`ef-${it.id}`}
                      title={meta.label}
                    >
                      {v === 'acquis' ? '✓' : v === 'renforcer' ? '!' : '·'}
                    </button>
                    <span className="text-[13px] text-white/85 print:text-black">{it.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* Placeholder verrouillé — s'affiche tant que les 5 séquences ne sont pas 100 % acquises.
   Rend l'expérience cohérente avec la progression pas à pas des tabs. */
function EvaluationFinaleLocked({ seqStatus, stats }) {
  return (
    <section
      className="mt-10 p-5 sm:p-6 rounded-2xl border-2 border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent print:hidden"
      data-testid="evaluation-finale-locked"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-[10px] font-black uppercase tracking-widest text-rose-300">
          <Lock className="w-3 h-3" /> Évaluation finale · verrouillée
        </span>
      </div>
      <h2
        className="text-xl sm:text-2xl font-black text-white/85 mb-2"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        Termine les 5 séquences pour débloquer l'évaluation finale
      </h2>
      <p className="text-[13px] text-white/60 leading-relaxed mb-4">
        L'évaluation finale « <em className="text-white/85 not-italic">Je sais conduire la Topolino</em> »
        porte sur l'ensemble du parcours. Elle apparaîtra automatiquement dès que
        les <strong className="text-white">36 fiches</strong> auront été marquées
        <span className="mx-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-black">Acquis</span>.
      </p>

      {/* Progression globale */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-widest text-white/60 mb-1.5">
          <span>Progression globale</span>
          <span className="text-white">{stats.acquis}/{stats.total} · {stats.pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-rose-500 via-amber-400 to-emerald-500 transition-all"
            style={{ width: `${stats.pct}%` }}
          />
        </div>
      </div>

      {/* Détail par séquence */}
      <ul className="grid gap-2 sm:grid-cols-2">
        {SEQUENCES.map((s) => {
          const st = seqStatus[s.id] || { total: 0, acquis: 0, complete: false, unlocked: false };
          const pct = st.total > 0 ? Math.round((st.acquis / st.total) * 100) : 0;
          const Icon = st.complete ? CheckCircle2 : st.unlocked ? Circle : Lock;
          return (
            <li
              key={s.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-black/20"
            >
              <Icon
                className={`w-4 h-4 flex-shrink-0 ${
                  st.complete
                    ? 'text-emerald-400'
                    : st.unlocked
                      ? 'text-white/45'
                      : 'text-rose-400/60'
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-black text-white/90 truncate">
                  S{s.num} · {s.title}
                </div>
                <div className="text-[10.5px] text-white/50">
                  {st.acquis}/{st.total} acquis · {pct}%
                </div>
              </div>
              {st.complete && (
                <span className="text-[9.5px] font-black uppercase tracking-widest text-emerald-400">
                  Terminée
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ReflexesFinaux() {
  return (
    <section className="mt-8 p-5 rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-600/10 via-amber-500/5 to-transparent">
      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-1 print:text-black">⭐ Mes 10 réflexes sécurité</div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5 mt-3">
        {REFLEXES.map((r, i) => (
          <div key={i} className="p-3 rounded-lg bg-black/30 border border-white/10 text-center print:bg-white print:border-black">
            <div className="text-2xl mb-1">{r.emoji}</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 print:text-black">Réflexe {i + 1}</div>
            <div className="text-[12.5px] font-black text-white mt-1 print:text-black">{r.txt}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function GrilleEnseignant({ state, save }) {
  return (
    <section className="mt-8 p-5 rounded-2xl border-2 border-sky-500/30 bg-gradient-to-br from-sky-600/10 via-sky-500/5 to-transparent print:break-before-page" data-testid="grille-enseignant">
      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-sky-400 mb-1 print:text-black">👨‍🏫 Grille enseignant officielle</div>
      <h2 className="text-2xl font-black text-white mb-2 print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Validation binaire · Arrêté 08/11/2012</h2>
      <p className="text-[12px] text-white/60 mb-4 print:text-black italic">1 = Compétence acquise · 2 = Compétence à renforcer</p>
      <div className="grid gap-4 md:grid-cols-2">
        {[GRILLE_ENSEIGNANT.s2, GRILLE_ENSEIGNANT.s4].map((block, bi) => (
          <div key={bi} className="p-4 rounded-xl border border-white/10 bg-black/20 print:bg-white print:border-black">
            <div className="text-[11px] font-black uppercase tracking-widest text-sky-400 mb-2 print:text-black">{block.title}</div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-white/50 print:text-black text-[10px] uppercase tracking-widest">
                  <th className="pb-2 font-black">Compétence</th>
                  <th className="pb-2 font-black w-24 text-center">Niveau</th>
                </tr>
              </thead>
              <tbody>
                {block.items.map((it, i) => {
                  const key = `grille-${bi}-${i}`;
                  const v = state[key] || 'todo';
                  const isAcquis = v === 'acquis';
                  const isRenforcer = v === 'renforcer';
                  return (
                    <tr key={i} className="border-t border-white/5 print:border-black">
                      <td className="py-1.5 text-white/85 print:text-black">{it}</td>
                      <td className="py-1.5 text-center">
                        <button type="button" onClick={() => save({ [key]: isAcquis ? 'todo' : 'acquis' })} className={`px-2 py-0.5 rounded text-[10px] font-black ${isAcquis ? 'bg-emerald-500 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'} print:bg-white print:text-black print:border print:border-black`} data-testid={`${key}-1`}>1</button>
                        <button type="button" onClick={() => save({ [key]: isRenforcer ? 'todo' : 'renforcer' })} className={`ml-1 px-2 py-0.5 rounded text-[10px] font-black ${isRenforcer ? 'bg-amber-500 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'} print:bg-white print:text-black print:border print:border-black`} data-testid={`${key}-2`}>2</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </section>
  );
}

function BilanFinal({ profil, setProfil }) {
  return (
    <section className="mt-8 p-5 rounded-2xl border-2 border-white/10 bg-white/[0.02] print:bg-white print:border-black print:break-inside-avoid" data-testid="bilan-final">
      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-rose-400 mb-1 print:text-black">🏆 Mon bilan</div>
      <h2 className="text-2xl font-black text-white mb-4 print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Synthèse de la formation</h2>
      <div className="grid gap-4 md:grid-cols-2">
        <BilanArea label="Ce que je sais faire" value={profil.bilanSaisFaire} onChange={(v) => setProfil({ bilanSaisFaire: v })} testid="bilan-savoir" />
        <BilanArea label="Ce que je dois encore améliorer" value={profil.bilanAmeliorer} onChange={(v) => setProfil({ bilanAmeliorer: v })} testid="bilan-ameliorer" />
        <BilanArea label="Mon principal risque personnel" value={profil.risquePerso} onChange={(v) => setProfil({ risquePerso: v })} testid="bilan-risque" />
        <BilanArea label="Mon objectif pour la suite" value={profil.objectifSuite} onChange={(v) => setProfil({ objectifSuite: v })} testid="bilan-objectif" />
      </div>
      <div className="mt-4 pt-4 border-t border-white/10 print:border-black">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/60 mb-2 print:text-black">📋 Bilan enseignant final</div>
        <div className="grid gap-3">
          <BilanArea label="Points forts" value={profil.pointsForts} onChange={(v) => setProfil({ pointsForts: v })} testid="pointsforts" />
          <BilanArea label="Compétences à renforcer" value={profil.aRenforcer} onChange={(v) => setProfil({ aRenforcer: v })} testid="arenforcer" />
          <BilanArea label="Comportements à surveiller" value={profil.aSurveiller} onChange={(v) => setProfil({ aSurveiller: v })} testid="asurveiller" />
          <BilanArea label="Conseils personnalisés" value={profil.conseils} onChange={(v) => setProfil({ conseils: v })} testid="conseils" />
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-white/10 print:border-black grid gap-3 sm:grid-cols-2">
        <div>
          <div className="text-[11px] font-black uppercase tracking-widest text-white/60 mb-1 print:text-black">Signature enseignant</div>
          <Input value={profil.signEnseignant || ''} onChange={(e) => setProfil({ signEnseignant: e.target.value })} className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black" data-testid="sign-enseignant" />
          <div className="mt-2 h-14 rounded border border-dashed border-white/20 print:border-black" />
        </div>
        <div>
          <div className="text-[11px] font-black uppercase tracking-widest text-white/60 mb-1 print:text-black">Signature élève</div>
          <Input value={profil.signEleve || ''} onChange={(e) => setProfil({ signEleve: e.target.value })} className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black" data-testid="sign-eleve" />
          <div className="mt-2 h-14 rounded border border-dashed border-white/20 print:border-black" />
        </div>
      </div>
    </section>
  );
}

function BilanArea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] font-black uppercase tracking-widest text-white/60 mb-1 print:text-black">{label}</div>
      <Textarea rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} className="bg-black/30 border-white/15 text-white print:bg-white print:text-black print:border-black" data-testid={testid} />
    </label>
  );
}
